// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/kalemler/kalemler-tablosu.tsx (yerleşim) + izgara-cekirdek.ts
// Fark: yalnız `EditableGridProps<R>` sözleşmesi; gruplama/donmuş kolon/genişlik sürükleme yok;
// satır-no oluğu tek donmuş kolon, sondaki boşluk kolonu satır eylemlerini taşır. Satır penceresi
// kendimizin (`use-grid-window.ts`): çok satırda yalnız görünenler çizilir.
import { useLayoutEffect, useRef, type ReactNode } from 'react'

import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

import { GridCell } from './GridCell'
import { isEditable } from './plan'
import { cellKey, inRange, isSingleCell } from './selection'
import type { EditableGridProps } from './types'
import { useGridCore } from './use-grid-core'
import { GRID_ROW_HEIGHT, useGridWindow } from './use-grid-window'

const t = messages.editableGrid

/** Row-number gutter width (px). */
const GUTTER_WIDTH = 48
/** Minimum width of the trailing column when it carries `rowActions` (px). */
const ACTIONS_WIDTH = 96

const HEAD_CELL =
  'bg-surface-subtle border-border sticky top-0 z-20 h-9 border-b px-2 text-xs font-medium'

/** Çizilmeyen satırların yerini tutan boşluk satırı: kaydırma çubuğu tüm listeyi gösterir. */
function Gap({ rows }: { rows: number }) {
  return (
    <tr aria-hidden="true" data-grid-gap="">
      <td colSpan={999} className="p-0" style={{ height: rows * GRID_ROW_HEIGHT }} />
    </tr>
  )
}

/** Artan dizinli pencere satırları; aralarına (ve başa/sona) boşluk satırı girer. */
function windowed(indexes: number[], total: number, render: (ri: number) => ReactNode) {
  const out: ReactNode[] = []
  let next = 0
  for (const ri of indexes) {
    if (ri > next) out.push(<Gap key={`gap-${next}`} rows={ri - next} />)
    out.push(render(ri))
    next = ri + 1
  }
  if (total > next) out.push(<Gap key={`gap-${next}`} rows={total - next} />)
  return out
}

export function EditableGrid<R>(props: EditableGridProps<R>) {
  const { ariaLabel, rows, columns, rowKey, issues, rowState, rowActions, readOnly, className } =
    props
  const [tableRef, core] = useGridCore(props)
  const { range, anchor, extent, editing } = core
  const scrollRef = useRef<HTMLDivElement>(null)
  const { indexes, scrollToRow } = useGridWindow({
    rowCount: rows.length,
    scrollRef,
    pinned: [anchor?.row, editing?.row],
  })
  const multi = range ? !isSingleCell(range) : false
  const minWidth =
    GUTTER_WIDTH + columns.reduce((sum, c) => sum + c.width, 0) + (rowActions ? ACTIONS_WIDTH : 0)

  // Seçimin hareket eden ucu çizilmemiş bir satırdaysa çekirdeğin `scrollIntoView`'u hücreyi
  // bulamaz: önce satır pencereye kaydırılır, çizildikten sonra hücre yatayda da görünür yapılır.
  const extentRow = extent?.row
  const extentCol = extent?.col
  useLayoutEffect(() => {
    if (!indexes || extentRow === undefined || extentCol === undefined) return
    const id = core.cellId(extentRow, extentCol)
    if (document.getElementById(id)) return
    scrollToRow(extentRow)
    const frame = requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }),
    )
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extentRow, extentCol])

  function renderRow(ri: number) {
    const row = rows[ri]!
    const key = rowKey(row)
    const rowIssues = issues?.[key]
    const hasIssue = !!rowIssues && Object.keys(rowIssues).length > 0
    const state = rowState?.(row)
    return (
      <tr key={key} role="row" aria-rowindex={ri + 2} data-row-key={key}>
        <td
          role="rowheader"
          aria-colindex={1}
          data-row-state={state}
          data-row-status={hasIssue ? 'error' : undefined}
          onMouseDown={(e) => {
            if (e.button !== 0) return
            e.preventDefault()
            core.selectRowAt(ri)
          }}
          className={cn(
            'bg-surface-subtle border-border-subtle text-muted-foreground sticky left-0 z-10 h-9 border-r border-b px-1 text-center text-xs tabular-nums select-none',
            state && 'text-primary font-medium',
          )}
        >
          <span className="inline-flex items-center gap-1">
            {hasIssue && (
              <>
                <span
                  aria-hidden="true"
                  className="bg-destructive inline-block size-1.5 rounded-full"
                />
                <span className="sr-only">{t.rowHasIssue}</span>
              </>
            )}
            {ri + 1}
          </span>
        </td>
        {columns.map((col, ci) => {
          const isActiveEditor = editing?.row === ri && editing.col === ci
          return (
            <GridCell
              key={col.id}
              id={core.cellId(ri, ci)}
              row={row}
              rowKey={key}
              rowIndex={ri}
              colIndex={ci}
              col={col}
              selected={!!range && inRange(range, ri, ci)}
              active={anchor?.row === ri && anchor.col === ci}
              multi={multi}
              editing={isActiveEditor ? { seed: editing.seed } : null}
              editable={isEditable(col, row, readOnly)}
              issue={rowIssues?.[col.id]}
              flashed={core.written.has(cellKey(key, col.id))}
              onMouseDown={(e) => core.cell.onCellMouseDown(e, ri, ci)}
              onMouseEnter={() => core.cell.onCellMouseEnter(ri, ci)}
              onDoubleClick={() => core.cell.onCellDoubleClick(ri, ci)}
              onOpenList={() => core.cell.openListAt(ri, ci)}
              onDone={core.closeEditor}
              onPicked={core.markPicked}
            />
          )
        })}
        <td className="border-border-subtle h-9 border-b px-1">
          {/* Buttons shrink to fit the 36px row; wrapping would multiply the row height. */}
          <div className="flex items-center gap-0.5 [&_button]:size-7">{rowActions?.(row)}</div>
        </td>
      </tr>
    )
  }

  return (
    // Opaque scroll container: a transparent one makes Chrome lose compositor scrolling.
    <div
      ref={scrollRef}
      className={cn(
        'border-border bg-background max-h-[70vh] overflow-auto rounded-lg border',
        className,
      )}
    >
      <table
        ref={tableRef}
        role="grid"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-rowcount={rows.length + 1}
        aria-colcount={columns.length + 2}
        aria-readonly={readOnly || undefined}
        aria-activedescendant={core.activeId}
        style={{ minWidth }}
        className="w-full table-fixed border-separate border-spacing-0 text-xs outline-none"
        {...core.handlers}
      >
        <colgroup>
          <col style={{ width: GUTTER_WIDTH }} />
          {columns.map((c) => (
            <col key={c.id} style={{ width: c.width }} />
          ))}
          {/* No width: the filler absorbs the extra space (table-layout:fixed would otherwise
              spread it over every column). */}
          <col />
        </colgroup>
        <thead>
          <tr role="row" aria-rowindex={1}>
            <th
              role="columnheader"
              aria-colindex={1}
              className={cn(HEAD_CELL, 'sticky left-0 z-30 text-center')}
            >
              <span className="sr-only">{t.rowHeader}</span>
            </th>
            {columns.map((c, ci) => (
              <th
                key={c.id}
                role="columnheader"
                aria-colindex={ci + 2}
                data-col-id={c.id}
                onMouseDown={(e) => {
                  if (e.button !== 0) return
                  e.preventDefault()
                  core.selectColumnAt(ci)
                }}
                className={cn(
                  HEAD_CELL,
                  'cursor-default truncate select-none',
                  (c.align ?? (c.kind === 'number' ? 'right' : 'left')) === 'right'
                    ? 'text-right'
                    : 'text-left',
                )}
              >
                {c.header}
              </th>
            ))}
            <th role="presentation" className={HEAD_CELL} />
          </tr>
        </thead>
        <tbody>
          {indexes ? windowed(indexes, rows.length, renderRow) : rows.map((_, ri) => renderRow(ri))}
        </tbody>
      </table>
    </div>
  )
}
